import { GS_APP_URLS } from '@gs/config/browser';

import type { AppDefinition, ComingApp } from '../types';

// LE CATALOGUE DU LANCEUR SE LIT DANS LE REGISTRE DES APPLICATIONS (ADR-090, 2026-10-07).
//
// QUI a une tuile : chaque application du registre de genre `application` (manifestes
// `apps/<app>/deploiement.json` et `registre/hors-depot/<app>.json`), hors le lanceur lui-meme.
// Le bloc genere ci-dessous en est la liste ; il ne s edite pas.
//
// COMMENT elle se presente : `PRESENTATION`, ecrite ici, une entree par cle du registre, dans
// l ordre d affichage. Son type `Record<CleDuRegistre, ...>` refuse a la compilation une
// application sans presentation ET une presentation sans application : une tuile ne survit
// pas a la sortie de son application du registre (la tuile « GS-Flotte » est partie ainsi :
// Flotte est une reservation, pas une application — ADR-090 regle 7).
//
// Les adresses viennent du registre partage (`GS_APP_URLS`, genere lui aussi) : la meme
// application avait deux cibles selon l endroit d ou on la cliquait
// (E-20260901-hub-admin-deux-cibles). Le chemin profond du portail reste ici : c est une route
// de CE catalogue, pas l adresse de l application.
//
// Historique garde : `inv` decommissionnee le 2026-09-26 (feat-decommissionner-inv) — sa cle
// `INV` est retiree au registre ; GS-Inventaires (Firebase, en service) a son manifeste hors
// depot et reste hors du lanceur faute d arbitrage (feat-reintegrer-le-poc-inventaire) ;
// GS-Analytics retiree le 2026-06-06, jamais une application du monorepo.

// registre:genere debut lanceur
// GENERE par npm run registre:generer depuis le registre des applications (ADR-090) — ne pas editer entre les marqueurs (lanceur).
export const APPLICATIONS_DU_REGISTRE = {
  admin: { url: GS_APP_URLS.admin, cycle: 'service', origine: 'depot' },
  'api-portail': { url: GS_APP_URLS.portail, cycle: 'service', origine: 'depot' },
  depenses: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  formation: { url: GS_APP_URLS.formation, cycle: 'service', origine: 'hors-depot' },
  gest: { url: GS_APP_URLS.gest, cycle: 'service', origine: 'depot' },
  inventaires: { url: '', cycle: 'service', origine: 'hors-depot' },
  ops: { url: GS_APP_URLS.ops, cycle: 'service', origine: 'hors-depot' },
  playlist: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  recettes: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  recrutement: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  salus: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  shophub: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  tickets: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  trivia: { url: '', cycle: 'naissance', origine: 'hors-depot' },
  'uf-webeditor': { url: GS_APP_URLS.ufWebEditor, cycle: 'service', origine: 'hors-depot' },
  ug: { url: GS_APP_URLS.ug, cycle: 'service', origine: 'depot' },
  zonesecur: { url: GS_APP_URLS.zonesecur, cycle: 'service', origine: 'depot' },
} as const;
// registre:genere fin lanceur

export type CleDuRegistre = keyof typeof APPLICATIONS_DU_REGISTRE;

/** Une tuile du catalogue : tout sauf l adresse, qui vient du registre ; `chemin` la prolonge. */
type TuileDuCatalogue = Omit<AppDefinition, 'url'> & { chemin?: string };

type Presentation =
  | { section: 'catalogue'; tuile: TuileDuCatalogue }
  | { section: 'a-venir'; tuile: ComingApp }
  | { section: 'hors-lanceur'; raison: string };

/** La presentation de chaque application du registre, dans l ordre d affichage. */
export const PRESENTATION: Record<CleDuRegistre, Presentation> = {
  gest: {
    section: 'catalogue',
    tuile: {
      id: 'gest',
      name: 'GS-Gestion',
      desc: 'Équipes, organigramme et votre fiche',
      icon: 'Users',
      iconBg: '#f0f9ff',
      iconColor: '#0284c7',
      platform: 'azure',
      status: 'prod',
    },
  },
  admin: {
    section: 'catalogue',
    tuile: {
      id: 'adm',
      name: 'GS-Admin',
      desc: 'Comptes, synchronisations et journaux',
      icon: 'Settings2',
      iconBg: '#eff6ff',
      iconColor: '#2563eb',
      platform: 'azure',
      status: 'prod',
      adminOnly: true,
    },
  },
  ug: {
    section: 'catalogue',
    tuile: {
      id: 'ug',
      name: 'Console UG',
      desc: 'Qui a accès à quoi',
      icon: 'KeyRound',
      iconBg: '#eff6ff',
      iconColor: '#2563eb',
      platform: 'azure',
      status: 'prod',
      adminOnly: true,
    },
  },
  'api-portail': {
    section: 'catalogue',
    tuile: {
      id: 'ptl',
      name: 'Portail',
      desc: 'Autocollants QR des véhicules',
      icon: 'QrCode',
      iconBg: '#fff7ed',
      iconColor: '#ea580c',
      platform: 'azure',
      status: 'prod',
      // Racine / = 404. Entree staff verifiee : /admin/login (302 vers Entra).
      chemin: '/admin/login',
    },
  },
  ops: {
    section: 'catalogue',
    tuile: {
      id: 'ops',
      name: 'GS-Ops',
      desc: 'GPS, pointage et bons de travail',
      icon: 'HardHat',
      iconBg: '#f0fdf4',
      iconColor: '#16a34a',
      platform: 'firebase', // platform a migrer vers azure post-ADR-052 (Phase 4 PWA terrain)
      // S.O.F.I. Connect 2.4 : ~75 utilisateurs actifs (PARC-APPLICATIF-2026-09-11 fiche 1).
      status: 'prod',
    },
  },
  shophub: {
    section: 'catalogue',
    tuile: {
      id: 'shp',
      name: 'GS-ShopHub',
      desc: 'Facturation mécanique B2B',
      icon: 'Wrench',
      iconBg: '#fdf4ff',
      iconColor: '#a21caf',
      platform: 'azure',
      status: 'poc',
    },
  },
  formation: {
    section: 'catalogue',
    tuile: {
      id: 'for',
      name: 'GS-Formation',
      desc: 'Formations et certifications',
      icon: 'GraduationCap',
      iconBg: '#f5f3ff',
      iconColor: '#7c3aed',
      platform: 'firebase', // platform a migrer vers azure post-ADR-052 (Phase 4 PWA terrain)
      // SOFI Formation : « Production, en phase test » (PARC fiche 5).
      status: 'test',
    },
  },
  zonesecur: {
    section: 'catalogue',
    tuile: {
      id: 'sst',
      name: 'GS-ZoneSecur',
      desc: 'Santé-sécurité et rapports terrain',
      icon: 'ShieldCheck',
      iconBg: '#ecfdf5',
      iconColor: '#059669',
      platform: 'firebase', // platform a migrer vers azure post-ADR-052 (Phase 4 PWA terrain)
      // GS-ZoneSecur : production, `main` == la production (PARC fiche 2).
      status: 'prod',
    },
  },
  'uf-webeditor': {
    section: 'catalogue',
    tuile: {
      id: 'iot',
      name: 'UF WebEditor',
      desc: 'Réglage des afficheurs de chantier',
      icon: 'Cpu',
      iconBg: '#fff8ed',
      iconColor: '#F99D27', // ds-lint-exempt: accent produit WebEditor (donnée de tuile, pas du styling de marque)
      platform: 'firebase', // ADR-052: reste firebase, IoT firmware Arduino REST Firestore (~80 devices OPTA en prod), isole intentionnel
      // WebEditor frontend 2.1.4 (PARC fiche 12) : l outil de reglage est en service.
      status: 'prod',
    },
  },
  depenses: {
    section: 'catalogue',
    tuile: {
      id: 'dep',
      name: 'GS-Dépenses',
      desc: 'Notes de frais et approbation',
      icon: 'Receipt',
      iconBg: '#fdf2f8',
      iconColor: '#c026d3',
      platform: 'azure',
      status: 'poc',
    },
  },
  salus: {
    section: 'catalogue',
    tuile: {
      id: 'sal',
      name: 'GS-Salus',
      desc: 'Inspections et risques EPI',
      icon: 'HeartPulse',
      iconBg: '#fef2f2',
      iconColor: '#dc2626',
      platform: 'firebase', // platform a migrer vers azure post-ADR-052 (Phase 4 PWA terrain)
      status: 'dev',
    },
  },

  // ─── Zone Espace GS (apps fun pour les employés) ───────────────
  recettes: {
    section: 'catalogue',
    tuile: {
      id: 'rec',
      name: 'GS-Recettes',
      desc: 'Recettes partagées entre collègues',
      icon: 'ChefHat',
      iconBg: '#fff7ed',
      iconColor: '#ea580c',
      platform: 'azure',
      status: 'poc',
      zone: 'fun',
    },
  },
  trivia: {
    section: 'catalogue',
    tuile: {
      id: 'trv',
      name: 'GS-Trivia',
      desc: 'Quiz sur l\'entreprise',
      icon: 'Brain',
      iconBg: '#eff6ff',
      iconColor: '#2563eb',
      platform: 'azure',
      status: 'poc',
      zone: 'fun',
    },
  },
  playlist: {
    section: 'catalogue',
    tuile: {
      id: 'pls',
      name: 'GS-Playlist',
      desc: 'Musique partagée au bureau',
      icon: 'Music2',
      iconBg: '#f5f3ff',
      iconColor: '#7c3aed',
      platform: 'azure',
      status: 'poc',
      zone: 'fun',
    },
  },

  recrutement: { section: 'a-venir', tuile: { id: 'rcr', name: 'GS-Recrutement', desc: 'Remplace Appy Here' } },
  tickets: { section: 'a-venir', tuile: { id: 'tkt', name: 'GS-Tickets', desc: 'Bugs et améliorations' } },

  inventaires: {
    section: 'hors-lanceur',
    raison: 'GS-Inventaires (Firebase) : hors du lanceur faute d arbitrage ; sa reintegration est le lot feat-reintegrer-le-poc-inventaire',
  },
};

/** L adresse d une tuile : celle du registre, prolongee de son chemin ; vide si l application n en a pas. */
function adresseDe(cle: CleDuRegistre, chemin: string | undefined): string {
  const url: string = APPLICATIONS_DU_REGISTRE[cle].url;
  return url && chemin ? `${url}${chemin}` : url;
}

const entrees = Object.entries(PRESENTATION) as [CleDuRegistre, Presentation][];

export const ALL_APPS: AppDefinition[] = entrees.flatMap(([cle, p]) => {
  if (p.section !== 'catalogue') return [];
  const { chemin, ...tuile } = p.tuile;
  return [{ ...tuile, url: adresseDe(cle, chemin) }];
});

export const COMING_APPS: ComingApp[] = entrees.flatMap(([, p]) => (p.section === 'a-venir' ? [p.tuile] : []));

export function getAppById(id: string): AppDefinition | undefined {
  return ALL_APPS.find((a) => a.id === id);
}
